import { Shield, Lock, Eye, Database } from 'lucide-react';
import LegalDocumentLayout from '@/components/LegalDocumentLayout';

const sections = [
  {
    id: 'introduction',
    title: '1. Introduction',
    shortTitle: 'Intro',
    summary: 'Our commitment to protecting your privacy.',
    content:
      'VibhumEduSys ("we", "our", or "us") is committed to protecting the privacy of schools, colleges, students, parents, and staff who use our school management software and related services. This Privacy Policy explains how we collect, use, store, and safeguard your information when you visit vibhumedusys.com or use our platform.',
  },
  {
    id: 'information-we-collect',
    title: '2. Information We Collect',
    shortTitle: 'Collection',
    summary: 'What data we gather and from where.',
    content:
      'We may collect information you provide directly, such as name, email address, phone number, school or institution details, and messages submitted through contact, demo, or registration forms.',
    points: [
      'Institutional data uploaded by schools (student, staff, and academic records).',
      'Technical data such as IP address, browser type, and usage logs.',
      'Information submitted through demos, registrations, and support requests.',
    ],
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Information',
    shortTitle: 'Usage',
    summary: 'How your information helps us serve you.',
    content:
      'We use collected information to provide and improve VibhumEduSys, respond to enquiries, schedule demos, create and manage accounts, send service-related communications, personalize your experience, detect and prevent fraud or abuse, and comply with applicable legal obligations.',
    points: ['We do not sell your personal information to third parties.'],
  },
  {
    id: 'data-sharing',
    title: '4. Data Sharing & Disclosure',
    shortTitle: 'Sharing',
    summary: 'When and why data may be shared.',
    content:
      'We may share information with trusted service providers who help us operate our website, hosting, email, and analytics—only to the extent needed for those services and under appropriate confidentiality obligations.',
    points: [
      'Disclosure when required by law, regulation, or legal process.',
      'Sharing to protect the rights, safety, and security of users and the public.',
    ],
  },
  {
    id: 'data-security',
    title: '5. Data Security',
    shortTitle: 'Security',
    summary: 'Measures we take to protect your data.',
    content:
      'We implement reasonable administrative, technical, and organizational measures to protect personal and institutional data against unauthorized access, alteration, disclosure, or destruction. While we strive to safeguard your information, no method of transmission or storage over the internet is completely secure.',
  },
  {
    id: 'data-retention',
    title: '6. Data Retention',
    shortTitle: 'Retention',
    summary: 'How long we keep your information.',
    content:
      'We retain personal and institutional data only for as long as necessary to fulfill the purposes described in this policy, provide our services, meet legal or contractual requirements, or resolve disputes. When data is no longer needed, we take steps to delete or anonymize it securely.',
  },
  {
    id: 'your-rights',
    title: '7. Your Rights',
    shortTitle: 'Rights',
    summary: 'Your choices and control over your data.',
    content:
      'Depending on applicable law, you may have the right to access, correct, update, or request deletion of your personal information, and to withdraw consent where processing is based on consent.',
    points: [
      'Institutions remain responsible for lawful processing of student and staff data.',
      'Contact us to exercise your privacy rights.',
    ],
  },
  {
    id: 'cookies',
    title: '8. Cookies & Tracking',
    shortTitle: 'Cookies',
    summary: 'How we use cookies on our website.',
    content:
      'Our website may use cookies and similar technologies to remember preferences, understand how visitors use the site, and improve functionality. You can control cookies through your browser settings; disabling cookies may affect certain features of the website.',
  },
  {
    id: 'children',
    title: "9. Children's Privacy",
    shortTitle: 'Children',
    summary: 'How student data is handled responsibly.',
    content:
      'VibhumEduSys is designed for educational institutions. Student data is processed on behalf of schools and colleges that use our platform. We do not knowingly collect personal information directly from children for marketing purposes.',
    points: [
      'Institutions must obtain required parental or guardian consents where applicable.',
    ],
  },
  {
    id: 'changes',
    title: '10. Changes to This Policy',
    shortTitle: 'Updates',
    summary: 'How we notify you of policy changes.',
    content:
      'We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements. The "Last updated" date at the top of this page will be revised when changes are made. Continued use of our website or services after an update constitutes acceptance of the revised policy.',
  },
  {
    id: 'contact',
    title: '11. Contact Us',
    shortTitle: 'Contact',
    summary: 'Reach out with privacy-related questions.',
    content:
      'If you have questions about this Privacy Policy or how we handle your data, please contact us at vibhumedusys@gmail.com or +91 96256 90427. Our office is located at Rohini Sector 21, New Delhi, India.',
  },
];

const highlights = [
  {
    icon: Shield,
    title: 'Protected by design',
    description: 'Security practices built into how we host and operate VibhumEduSys.',
    targetId: 'data-security',
  },
  {
    icon: Lock,
    title: 'No data selling',
    description: 'We never sell your personal or institutional information to third parties.',
    targetId: 'how-we-use',
  },
  {
    icon: Eye,
    title: 'Transparent use',
    description: 'Clear explanation of what we collect and why we need it.',
    targetId: 'information-we-collect',
  },
  {
    icon: Database,
    title: 'Institutional control',
    description: 'Schools and colleges remain in control of the data in their accounts.',
    targetId: 'your-rights',
  },
];

export default function PrivacyPage() {
  return (
    <LegalDocumentLayout
      testId="privacy-page"
      title="Privacy Policy"
      description="How VibhumEduSys collects, uses, and protects information across our website and school management platform."
      lastUpdated="July 10, 2026"
      icon={Shield}
      highlights={highlights}
      sections={sections}
      contactTitle="Questions about privacy?"
      contactDescription="Reach out anytime—we are happy to clarify how your data is handled."
    />
  );
}
